import { useMutation, useQuery } from '@apollo/client';
import { ArrowLeft, Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { CustomerFormDialog } from './CustomerFormDialog';
import { CUSTOMER_QUERY, SET_CUSTOMER_STATUS_MUTATION } from './customers.gql';
import { PetFormDialog } from './PetFormDialog';
import { MARK_PET_DECEASED_MUTATION, PETS_QUERY, SET_PET_STATUS_MUTATION } from './pets.gql';

import { CustomerFiscalProfilesTab } from '@/modules/invoicing/CustomerFiscalProfilesTab';
import { TotemMark } from '@/shared/brand/TotemLogo';
import { assertMutationSucceeded, errorMessage } from '@/shared/api/graphql-errors';
import { CUSTOMER_STATUS_FROM_GQL, PET_SEX_FROM_GQL } from '@/shared/graphql/enums';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { ConfirmDialog } from '@/shared/ui/confirm-dialog';
import { showToast } from '@/shared/ui/toast';
import { useAuthStore } from '@/stores/authStore';

interface Customer {
  id: string;
  first_name: string;
  last_name: string | null;
  phone: string;
  email: string | null;
  rfc: string | null;
  tax_regime: string | null;
  postal_code: string | null;
  cfdi_usage: string | null;
  fiscal_legal_name: string | null;
  fiscal_email: string | null;
  notes: string | null;
  status: string;
}

interface Pet {
  id: string;
  customer_id: string;
  name: string;
  species: string | null;
  breed: string | null;
  color: string | null;
  sex: string;
  birth_date: string | null;
  approximate_age_months: number | null;
  weight_grams: number | null;
  is_deceased: boolean;
  deceased_at: string | null;
  status: 'active' | 'inactive';
  notes: string | null;
  record: { id: string; code: string; status: string } | null;
}

export function CustomerDetailPage(): JSX.Element {
  const { t } = useTranslation();
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const can_write = user?.permissions.includes('customers.write') ?? false;

  const [editing_customer, setEditingCustomer] = useState(false);
  const [creating_pet, setCreatingPet] = useState(false);
  const [editing_pet, setEditingPet] = useState<Pet | null>(null);
  const [deceased_pet, setDeceasedPet] = useState<Pet | null>(null);

  const { data: customer_data, refetch: refetchCustomer } = useQuery<{ customer: Customer }>(
    CUSTOMER_QUERY,
    { variables: { id } },
  );
  const { data: pets_data, refetch: refetchPets } = useQuery<{
    pets: { items: Pet[] };
  }>(PETS_QUERY, {
    variables: { customer_id: id, limit: 50, status_filter: 'ALL', include_deceased: true },
  });

  const [setCustomerStatus] = useMutation(SET_CUSTOMER_STATUS_MUTATION);
  const [setPetStatus] = useMutation(SET_PET_STATUS_MUTATION);
  const [markDeceased, { loading: marking_deceased }] = useMutation(MARK_PET_DECEASED_MUTATION);

  const raw_customer = customer_data?.customer;
  const customer = raw_customer
    ? {
        ...raw_customer,
        status:
          CUSTOMER_STATUS_FROM_GQL[
            raw_customer.status as keyof typeof CUSTOMER_STATUS_FROM_GQL
          ] ?? raw_customer.status,
      }
    : undefined;
  const pets = (pets_data?.pets.items ?? []).map((pet) => ({
    ...pet,
    sex: PET_SEX_FROM_GQL[pet.sex as keyof typeof PET_SEX_FROM_GQL] ?? 'unknown',
  }));

  const onClickToggleCustomer = async (): Promise<void> => {
    if (!customer) return;
    try {
      const result = await setCustomerStatus({
        variables: { id: customer.id, status: customer.status === 'active' ? 'inactive' : 'active' },
      });
      assertMutationSucceeded(result, 'update_customer_status_failed');
      showToast({
        tone: 'success',
        title: t('alerts.update_success_title'),
        body: t('alerts.update_success_body'),
      });
      await refetchCustomer();
    } catch (err) {
      showToast({
        tone: 'error',
        title: t('alerts.update_error_title'),
        body: errorMessage(err),
      });
    }
  };

  const onClickTogglePet = async (pet: Pet): Promise<void> => {
    try {
      const result = await setPetStatus({
        variables: { id: pet.id, status: pet.status === 'active' ? 'inactive' : 'active' },
      });
      assertMutationSucceeded(result, 'update_pet_status_failed');
      showToast({
        tone: 'success',
        title: t('alerts.update_success_title'),
        body: t('alerts.update_success_body'),
      });
      await refetchPets();
    } catch (err) {
      showToast({
        tone: 'error',
        title: t('alerts.update_error_title'),
        body: errorMessage(err),
      });
    }
  };

  const onClickMarkDeceased = (pet: Pet): void => {
    setDeceasedPet(pet);
  };

  const onConfirmMarkDeceased = async (): Promise<void> => {
    if (!deceased_pet) return;
    try {
      const result = await markDeceased({ variables: { id: deceased_pet.id } });
      assertMutationSucceeded(result, 'mark_pet_deceased_failed');
      setDeceasedPet(null);
      showToast({
        tone: 'success',
        title: t('alerts.update_success_title'),
        body: t('pets.mark_deceased_success'),
      });
      await refetchPets();
    } catch (err) {
      showToast({
        tone: 'error',
        title: t('alerts.update_error_title'),
        body: errorMessage(err),
      });
    }
  };

  if (!customer) {
    return <div aria-busy="true">{t('common.loading')}</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Button type="button" variant="ghost" size="sm" onClick={() => navigate('/app/customers')}>
          <ArrowLeft className="h-4 w-4" aria-hidden />
          <span>{t('customers.back_to_list')}</span>
        </Button>
        {can_write ? (
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => setEditingCustomer(true)}>
              {t('common.edit')}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                void onClickToggleCustomer();
              }}
            >
              {customer.status === 'active' ? t('common.deactivate') : t('common.activate')}
            </Button>
          </div>
        ) : null}
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle>
                {customer.first_name}
                {customer.last_name ? ` ${customer.last_name}` : ''}
              </CardTitle>
              <CardDescription className="font-mono">{customer.phone}</CardDescription>
            </div>
            <Badge variant={customer.status === 'active' ? 'success' : 'muted'}>
              {t(`status.${customer.status}`)}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="grid gap-3 text-sm md:grid-cols-2">
          <div>
            <span className="text-muted-foreground">{t('customers.email')}: </span>
            <span>{customer.email ?? '—'}</span>
          </div>
          <div>
            <span className="text-muted-foreground">{t('customers.rfc')}: </span>
            <span className="font-mono">{customer.rfc ?? '—'}</span>
          </div>
          <div>
            <span className="text-muted-foreground">{t('customers.tax_regime')}: </span>
            <span>{customer.tax_regime ?? '—'}</span>
          </div>
          <div>
            <span className="text-muted-foreground">{t('customers.postal_code')}: </span>
            <span>{customer.postal_code ?? '—'}</span>
          </div>
          <div>
            <span className="text-muted-foreground">{t('customers.cfdi_usage')}: </span>
            <span>{customer.cfdi_usage ?? '—'}</span>
          </div>
          <div>
            <span className="text-muted-foreground">{t('customers.fiscal_legal_name')}: </span>
            <span>{customer.fiscal_legal_name ?? '—'}</span>
          </div>
          {customer.notes ? (
            <div className="md:col-span-2">
              <p className="text-muted-foreground">{t('customers.notes')}</p>
              <p className="whitespace-pre-line">{customer.notes}</p>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <CustomerFiscalProfilesTab customer_id={customer.id} />

      <section>
        <header className="mb-2 flex items-end justify-between">
          <div>
            <h3 className="font-display text-lg font-bold tracking-tight">{t('pets.title')}</h3>
            <p className="text-sm text-muted-foreground">{t('pets.subtitle')}</p>
          </div>
          {can_write ? (
            <Button type="button" onClick={() => setCreatingPet(true)}>
              <Plus className="h-4 w-4" aria-hidden />
              <span>{t('pets.create')}</span>
            </Button>
          ) : null}
        </header>

        {pets.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center gap-2 py-8 text-center text-sm text-muted-foreground">
              <TotemMark className="h-10 w-auto text-muted-foreground/40" aria-hidden />
              {t('pets.empty_for_customer')}
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {pets.map((pet) => (
              <Card key={pet.id} className={pet.is_deceased ? 'opacity-70' : undefined}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-base">{pet.name}</CardTitle>
                      <CardDescription>
                        {[pet.species, pet.breed, pet.color].filter(Boolean).join(' · ') || '—'}
                      </CardDescription>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {pet.is_deceased ? (
                        <Badge variant="destructive">{t('pets.deceased')}</Badge>
                      ) : (
                        <Badge variant={pet.status === 'active' ? 'success' : 'muted'}>
                          {t(`status.${pet.status}`)}
                        </Badge>
                      )}
                      {pet.record ? (
                        <span className="font-mono text-xs text-muted-foreground">
                          {pet.record.code}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="grid grid-cols-2 gap-2 text-muted-foreground">
                    <span>{t('pets.sex')}: <span className="text-foreground">{t(`pets.sex_${pet.sex}`)}</span></span>
                    <span>
                      {t('pets.age_months')}:{' '}
                      <span className="text-foreground">{pet.approximate_age_months ?? '—'}</span>
                    </span>
                    <span>
                      {t('pets.weight_grams')}:{' '}
                      <span className="text-foreground">{pet.weight_grams ?? '—'}</span>
                    </span>
                    <span>
                      {t('pets.birth_date')}:{' '}
                      <span className="text-foreground">{pet.birth_date ?? '—'}</span>
                    </span>
                  </div>
                  {can_write && !pet.is_deceased ? (
                    <div className="flex flex-wrap gap-1 pt-2">
                      <Button type="button" variant="ghost" size="sm" onClick={() => setEditingPet(pet)}>
                        {t('common.edit')}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          void onClickTogglePet(pet);
                        }}
                      >
                        {pet.status === 'active' ? t('common.deactivate') : t('common.activate')}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-destructive"
                        onClick={() => onClickMarkDeceased(pet)}
                      >
                        {t('pets.mark_deceased')}
                      </Button>
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>

      {editing_customer ? (
        <CustomerFormDialog
          mode="edit"
          initial_values={{
            id: customer.id,
            first_name: customer.first_name,
            last_name: customer.last_name ?? '',
            phone: customer.phone,
            email: customer.email ?? '',
            rfc: customer.rfc ?? '',
          }}
          onClose={() => setEditingCustomer(false)}
          onSaved={async () => {
            setEditingCustomer(false);
            showToast({
              tone: 'success',
              title: t('alerts.update_success_title'),
              body: t('alerts.update_success_body'),
            });
            await refetchCustomer();
          }}
        />
      ) : null}

      {creating_pet ? (
        <PetFormDialog
          mode="create"
          customer_id={customer.id}
          onClose={() => setCreatingPet(false)}
          onSaved={async () => {
            setCreatingPet(false);
            showToast({
              tone: 'success',
              title: t('alerts.create_success_title'),
              body: t('alerts.create_success_body'),
            });
            await refetchPets();
          }}
        />
      ) : null}

      {editing_pet ? (
        <PetFormDialog
          mode="edit"
          customer_id={customer.id}
          initial_pet={editing_pet}
          onClose={() => setEditingPet(null)}
          onSaved={async () => {
            setEditingPet(null);
            showToast({
              tone: 'success',
              title: t('alerts.update_success_title'),
              body: t('alerts.update_success_body'),
            });
            await refetchPets();
          }}
        />
      ) : null}

      <ConfirmDialog
        open={deceased_pet !== null}
        title={t('pets.confirm_mark_deceased_title')}
        description={t('pets.confirm_mark_deceased', { name: deceased_pet?.name ?? '' })}
        confirm_label={t('common.confirm')}
        cancel_label={t('common.cancel')}
        loading_label={t('common.loading')}
        loading={marking_deceased}
        destructive
        on_cancel={() => setDeceasedPet(null)}
        on_confirm={onConfirmMarkDeceased}
      />
    </div>
  );
}
